import { useMemo } from "react";
import {
	hasMeasurableWorkflowElapsed,
	isFailedSubagentStatus,
	isTerminalWorkflowStatus,
	workflowFailureText,
	workflowStatusText,
	workflowTitle,
	type ChatSubagentRun,
	type ChatWorkflowRun,
} from "@/lib/chat";
import { formatRunElapsed, type ToolUseRenderBlock } from "@/lib/chat-render";
import { useTick } from "@/lib/useTick";
import { groupWorkflowNodes, type WorkflowNodeEntry } from "@/lib/workflow-tasks";
import { useChatStore } from "@/stores/useChatStore";
import { useLayoutStore } from "@/stores/useLayoutStore";
import { WorkflowProgressDots } from "./WorkflowProgressDots";
import { WorkflowStopButton } from "./WorkflowStopButton";

/**
 * 投入した DAG の宣言から、表示に使う分だけを読む。workflow 行が届くまでは見出しもノードの輪郭も
 * ここからしか取れない。
 */
function definitionSummary(input: Record<string, unknown>): {
	title: string | null;
	nodes: WorkflowNodeEntry[];
} {
	const definition = input.definition;
	if (typeof definition !== "object" || definition === null) return { title: null, nodes: [] };
	const { name, description, nodes } = definition as {
		name?: unknown;
		description?: unknown;
		nodes?: unknown;
	};
	const title =
		typeof name === "string" && name
			? name
			: typeof description === "string" && description
				? description
				: null;
	const declared = Array.isArray(nodes)
		? nodes.flatMap((node, index): WorkflowNodeEntry[] => {
				if (typeof node !== "object" || node === null) return [];
				const { id, description } = node as { id?: unknown; description?: unknown };
				return [
					{
						id: typeof id === "string" ? id : String(index),
						description: typeof description === "string" ? description : "",
						run: null,
					},
				];
			})
		: [];
	return { title, nodes: declared };
}

/**
 * ノードの輪郭。workflow 行が届いていれば宣言と所有 run を突き合わせ、届く前は投入時の定義から
 * 起きていないノードとして描く。サブエージェント一覧だけが未取得の間は null — 全ノードを未着手と
 * 描くと、ほぼ終わった DAG が「何も始まっていない」に見える。
 */
function resolveNodes(
	run: ChatWorkflowRun | undefined,
	runs: ChatSubagentRun[] | null,
	declaredNodes: WorkflowNodeEntry[],
): WorkflowNodeEntry[] | null {
	if (run === undefined) return declaredNodes;
	if (runs === null) return null;
	return groupWorkflowNodes(run, runs).nodes;
}

/** 起動済みの run_workflow を描くカード。進捗の詳細はタスクタブへ繋ぐ。 */
export function WorkflowCard({ tools }: { tools: ToolUseRenderBlock[] }) {
	return (
		<div data-testid="workflow-card" className="min-w-0 grid gap-1.5">
			{tools.map((tool) => (
				<WorkflowRow key={tool.key} tool={tool} />
			))}
		</div>
	);
}

function WorkflowRow({ tool }: { tool: ToolUseRenderBlock }) {
	const openBottomPaneTab = useLayoutStore((state) => state.openBottomPaneTab);
	const runs = useChatStore((state) => state.subagentRuns);
	const run = tool.workflowRun;
	const declared = useMemo(() => definitionSummary(tool.input), [tool.input]);
	const nodes = useMemo(() => resolveNodes(run, runs, declared.nodes), [run, runs, declared]);
	const title = run ? workflowTitle(run) : (declared.title ?? "ワークフロー");
	// 内訳が未取得でも総数は宣言から分かる。
	const nodeCount = nodes?.length ?? run?.declaredNodes.length ?? declared.nodes.length;
	const failureText = run ? workflowFailureText(run) : null;
	return (
		<div
			data-testid="workflow-row"
			data-status={tool.status}
			className="min-w-0 rounded-4 bg-surface-1 px-3 py-2.5 shadow-ring"
		>
			<div className="flex min-w-0 items-center gap-2">
				<button
					type="button"
					onClick={() => openBottomPaneTab("tasks")}
					className="min-w-0 flex-1 cursor-pointer truncate border-0 bg-transparent p-0 text-left text-ui leading-body text-fg-1 [font-family:inherit]"
					data-tooltip={`${title}\nタスクタブでノードごとの進捗を見る`}
				>
					{title}
				</button>
				{run && <WorkflowStopButton run={run} />}
			</div>
			<p className="m-0 mt-0.5 text-caption text-fg-3">
				<WorkflowMeta run={run} nodes={nodes} nodeCount={nodeCount} />
			</p>
			{nodes !== null && <WorkflowProgressDots nodes={nodes} className="mt-2" />}
			{failureText && (
				<p data-testid="workflow-error" className="m-0 mt-1 text-caption text-danger wrap-anywhere">
					{failureText}
				</p>
			)}
		</div>
	);
}

function metaText(
	run: ChatWorkflowRun | undefined,
	nodes: WorkflowNodeEntry[] | null,
	nodeCount: number,
	now: number,
): string {
	const parts = ["ワークフロー"];
	if (nodeCount > 0) parts.push(`${nodeCount}エージェント`);
	// 一覧が届くまで workflow の状態は分からない。投入済みであることだけを伝える。
	if (!run) return [...parts, "起動中"].join(" · ");
	// 走っている間は丸と経過が状態そのもの。終端したときだけ言葉でも添える。
	if (isTerminalWorkflowStatus(run.status)) parts.push(workflowStatusText(run.status));
	// 丸は小さいので、失敗したノードの数は文字でも添える。内訳が未取得の間は伏せる。
	const failed = nodes?.filter(
		(node) => node.run && isFailedSubagentStatus(node.run.status),
	).length;
	if (failed !== undefined && failed > 0) parts.push(`${failed}件失敗`);
	const elapsed = hasMeasurableWorkflowElapsed(run)
		? formatRunElapsed(run.startedAt, run.endedAt, now)
		: "";
	if (elapsed) parts.push(elapsed);
	return parts.join(" · ");
}

/** 終端していない workflow は経過が伸び続けるので、その間だけ毎秒の tick を購読する。 */
function WorkflowMeta({
	run,
	nodes,
	nodeCount,
}: {
	run: ChatWorkflowRun | undefined;
	nodes: WorkflowNodeEntry[] | null;
	nodeCount: number;
}) {
	if (run !== undefined && !isTerminalWorkflowStatus(run.status))
		return <TickingMeta run={run} nodes={nodes} nodeCount={nodeCount} />;
	// 行が無ければ経過は出さない。終端した行は endedAt を持つため now は参照されない。
	return <>{metaText(run, nodes, nodeCount, 0)}</>;
}

function TickingMeta({
	run,
	nodes,
	nodeCount,
}: {
	run: ChatWorkflowRun;
	nodes: WorkflowNodeEntry[] | null;
	nodeCount: number;
}) {
	const now = useTick(1_000);
	return <>{metaText(run, nodes, nodeCount, now)}</>;
}
